import { z } from 'zod'

import { TicketStatusTypeEnum } from './crm-stage'
import { ModuleTypeEnum } from './module-type'

export enum TicketPriorityEnum {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

export enum TicketSourceEnum {
  INTERNAL = 'INTERNAL',
  FORM = 'FORM',
  PORTAL = 'PORTAL',
  EMAIL = 'EMAIL',
  WORKFLOW = 'WORKFLOW',
  IMPORT = 'IMPORT',
}

/**
 * Sources that support org-level "alert these users when a new ticket
 * arrives" subscriptions (Tickets settings → Alerts). Inbox-routed email
 * tickets additionally notify the inbox's own notify members.
 */
export const TICKET_ALERT_SOURCES = [
  TicketSourceEnum.FORM,
  TicketSourceEnum.PORTAL,
  TicketSourceEnum.EMAIL,
] as const

/** Map of ticket source → user ids subscribed to new-ticket alerts. */
export type TicketAlertSubscriptionMap = Record<string, string[]>

export const SYSTEM_TICKET_SOURCE_VALUES = Object.values(TicketSourceEnum)

export const TicketSourceDefinitionSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  value: z.string().min(1).max(30),
  label: z.string().min(1).max(100),
  description: z.string().nullable().optional(),
  isSystem: z.boolean().default(false),
  isEnabled: z.boolean().default(true),
  order: z.number().int().default(0),
  usageCount: z.number().int().nonnegative().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
})

export type TicketSourceDefinition = z.infer<
  typeof TicketSourceDefinitionSchema
>

export const UpsertTicketSourceDefinitionSchema = z.object({
  id: z.string().uuid().optional(),
  value: z.string().min(1).max(30).optional(),
  label: z.string().min(1).max(100),
  description: z.string().max(500).nullable().optional(),
  isSystem: z.boolean().optional(),
  isEnabled: z.boolean().default(true),
  order: z.number().int().default(0),
})

export type UpsertTicketSourceDefinition = z.infer<
  typeof UpsertTicketSourceDefinitionSchema
>

export const SetTicketSourceDefinitionsSchema = z.object({
  sources: z.array(UpsertTicketSourceDefinitionSchema),
})

export type SetTicketSourceDefinitions = z.infer<
  typeof SetTicketSourceDefinitionsSchema
>

export { TicketStatusTypeEnum }

export const TicketReferenceSchema = z.object({
  id: z.string().uuid().optional(),
  organizationId: z.string().uuid().optional(),
  ticketId: z.string().uuid().optional(),
  moduleType: z.nativeEnum(ModuleTypeEnum),
  moduleId: z.string().uuid(),
  label: z.string().nullable().optional(),
  createdAt: z.date().optional(),
})

export type TicketReference = z.infer<typeof TicketReferenceSchema>

export const TicketAssignmentSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  ticketId: z.string().uuid(),
  userId: z.string().uuid(),
  assignedAt: z.date(),
  assignedBy: z.string().uuid().nullable().optional(),
  user: z.any().optional(),
})

export type TicketAssignment = z.infer<typeof TicketAssignmentSchema>

export const TicketFollowerSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  ticketId: z.string().uuid(),
  userId: z.string().uuid(),
  followedAt: z.date(),
  user: z.any().optional(),
})

export type TicketFollower = z.infer<typeof TicketFollowerSchema>

// A file staged for a ticket message (create or reply). The client uploads
// the file first (skip-document mode) and passes back the resulting S3
// storage key plus display metadata; the backend persists these as
// CommunicationAttachment rows on the relevant communication.
export const TicketAttachmentInputSchema = z.object({
  storageKey: z.string().min(1).max(500),
  fileName: z.string().min(1).max(255),
  fileSize: z.number().int().nonnegative(),
  mimeType: z.string().min(1).max(150),
})

export type TicketAttachmentInput = z.infer<typeof TicketAttachmentInputSchema>

// Lightweight summary of a task a ticket was promoted to. Resolved on the
// list and detail endpoints from the polymorphic ticket_references (moduleType
// TASK) plus a batched task lookup, so callers can render a clickable
// reference without the full reference/task payload. Read-only; never part of
// a ticket write.
export const LinkedTaskSummarySchema = z.object({
  id: z.string().uuid(),
  taskNumber: z.string().nullable().optional(),
  name: z.string(),
  // Status category (TaskStatusTypeEnum) — what drives linked-task progress
  // and the ticket's own task sync. Not the org's custom status name.
  statusTypeId: z.string().nullable().optional(),
})

export type LinkedTaskSummary = z.infer<typeof LinkedTaskSummarySchema>

export const TicketSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  ticketNumber: z.string(),
  subject: z.string(),
  description: z.string().nullable().optional(),
  stage: z.string().default('new'),
  stageTypeId: z.nativeEnum(TicketStatusTypeEnum).nullable().optional(),
  // Modular ticket type (org-defined classification). Nullable for legacy rows.
  typeDefId: z.string().uuid().nullable().optional(),
  requesterCount: z.number().int().optional(),
  requesterWeight: z.number().nullable().optional(),
  slaDueAt: z.coerce.date().nullable().optional(),
  // SLA lifecycle (see ticket-sla.ts). All server-managed, read-only.
  slaFirstResponseDueAt: z.coerce.date().nullable().optional(),
  firstRespondedAt: z.coerce.date().nullable().optional(),
  slaPausedAt: z.coerce.date().nullable().optional(),
  slaTotalPausedMinutes: z.number().int().optional(),
  slaResolutionEscalationLevel: z.number().int().optional(),
  slaFirstResponseEscalationLevel: z.number().int().optional(),
  // Snooze: hidden from default agent views until this time, a client
  // reply, or manual unsnooze. Managed via the snooze endpoints.
  snoozedUntil: z.coerce.date().nullable().optional(),
  snoozedBy: z.string().uuid().nullable().optional(),
  // Denormalized CSAT rating (1 = bad, 2 = okay, 3 = good); source of truth
  // is the ticket's satisfaction survey (ticket-csat.ts).
  csatRating: z.number().int().nullable().optional(),
  isPrivate: z.boolean().optional(),
  priority: z.nativeEnum(TicketPriorityEnum).default(TicketPriorityEnum.MEDIUM),
  source: z.string().default(TicketSourceEnum.INTERNAL),
  dueDate: z.date().nullable().optional(),
  requesterName: z.string().nullable().optional(),
  requesterEmail: z.string().nullable().optional(),
  requesterUserId: z.string().uuid().nullable().optional(),
  requesterContactId: z.string().uuid().nullable().optional(),
  requesterEntityId: z.string().uuid().nullable().optional(),
  createdBy: z.string().uuid().nullable().optional(),
  resolvedAt: z.date().nullable().optional(),
  closedAt: z.date().nullable().optional(),
  // Free-form classification tags. Applied automatically by per-inbox
  // defaults on creation; admins can also edit. Backed by the Postgres
  // tags text[] column added in migration 20260517190000. Optional in
  // the schema so non-tag-aware callers don't have to opt-in.
  tags: z.array(z.string()).optional(),
  settings: z.any().default({}),
  // Which inbox routed/intook this ticket (email routing or intake widget).
  // Drives the From address for outbound replies and inbox member alerts.
  sourceInboxId: z.string().uuid().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
  assignments: z.array(TicketAssignmentSchema).optional(),
  followers: z.array(TicketFollowerSchema).optional(),
  references: z.array(TicketReferenceSchema).optional(),
  // Read-only compatibility field containing the first task created from the
  // ticket. New callers should prefer linkedTasks, which includes the full
  // ticket split.
  linkedTask: LinkedTaskSummarySchema.nullable().optional(),
  linkedTasks: z.array(LinkedTaskSummarySchema).optional(),
  customFields: z.record(z.any()).optional(),
  commentCount: z.number().optional(),
})

export type Ticket = z.infer<typeof TicketSchema>

export const CreateTicketSchema = TicketSchema.omit({
  id: true,
  ticketNumber: true,
  stageTypeId: true,
  requesterCount: true,
  requesterWeight: true,
  slaDueAt: true,
  slaFirstResponseDueAt: true,
  firstRespondedAt: true,
  slaPausedAt: true,
  slaTotalPausedMinutes: true,
  slaResolutionEscalationLevel: true,
  slaFirstResponseEscalationLevel: true,
  snoozedUntil: true,
  snoozedBy: true,
  csatRating: true,
  resolvedAt: true,
  closedAt: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  assignments: true,
  followers: true,
  linkedTask: true,
  linkedTasks: true,
}).extend({
  organizationId: z.string().uuid().optional(),
  ticketNumber: z.string().optional(),
  stage: z.string().optional(),
  stageTypeId: z.nativeEnum(TicketStatusTypeEnum).optional(),
  assigneeIds: z.array(z.string().uuid()).optional(),
  followerIds: z.array(z.string().uuid()).optional(),
  references: z.array(TicketReferenceSchema).optional(),
  customFields: z.record(z.any()).optional(),
  // Client-generated id used to scope inline-image uploads before the ticket
  // exists. On create, Documents uploaded under this id are re-pointed to the
  // real ticket. Not persisted on the ticket itself.
  draftId: z.string().uuid().optional(),
  // Files staged under `draftId` (skip-document uploads). When present, create
  // seeds an initial inbound communication carrying these as attachments.
  attachments: z.array(TicketAttachmentInputSchema).max(25).optional(),
})

export type CreateTicket = z.infer<typeof CreateTicketSchema>

export const UpdateTicketSchema = CreateTicketSchema.partial().omit({
  organizationId: true,
  ticketNumber: true,
})

export type UpdateTicket = z.infer<typeof UpdateTicketSchema>

// =====================
// Ticket Drafts
// =====================

// A draft can be half-filled, so every field is optional. The payload mirrors
// the create form (CreateTicket / CreatePortalTicketDTO shape) and is only
// validated against the strict CreateTicketSchema on submit, never on save.
export const TicketDraftPayloadSchema = CreateTicketSchema.partial()

export type TicketDraftPayload = z.infer<typeof TicketDraftPayloadSchema>

// Upsert body (PUT). The client generates `id` so it doubles as the
// upload-scope id for inline images/attachments staged before the ticket
// exists. `subject` is denormalized for fast list rendering.
export const UpsertTicketDraftSchema = z.object({
  subject: z.string().max(255).nullable().optional(),
  payload: TicketDraftPayloadSchema.default({}),
})

export type UpsertTicketDraft = z.infer<typeof UpsertTicketDraftSchema>

export const TicketDraftSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  source: z.string(),
  requesterEntityId: z.string().uuid().nullable().optional(),
  subject: z.string().nullable().optional(),
  payload: TicketDraftPayloadSchema,
  createdBy: z.string().uuid().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type TicketDraft = z.infer<typeof TicketDraftSchema>

// Compact row for the Drafts list (no full payload).
export interface TicketDraftListItem {
  id: string
  subject: string | null
  updatedAt: string
  createdAt: string
}

// ---- Reply drafts ----------------------------------------------------------
//
// An unsent reply composer on an existing ticket, one live row per
// (ticket, author, surface). Server-persisted so a half-written reply follows
// the user across devices and is deleted the moment the reply is sent.

// Upsert body (PUT). Saving an empty draft (no text, no cc, no attachments)
// deletes the stored row instead — "no draft" and "empty draft" are the same
// state.
export const UpsertTicketReplyDraftSchema = z.object({
  bodyHtml: z.string().max(1_000_000).default(''),
  ccText: z.string().max(2000).default(''),
  // Upload scope for staged attachments (validated as the storage-key
  // prefix on send), persisted with the attachments it scopes.
  replyDraftId: z.string().uuid(),
  attachments: z.array(TicketAttachmentInputSchema).max(25).default([]),
})

export type UpsertTicketReplyDraft = z.infer<
  typeof UpsertTicketReplyDraftSchema
>

export const TicketReplyDraftSchema = z.object({
  ticketId: z.string().uuid(),
  bodyHtml: z.string(),
  ccText: z.string(),
  replyDraftId: z.string().uuid(),
  attachments: z.array(TicketAttachmentInputSchema),
  updatedAt: z.coerce.date(),
})

export type TicketReplyDraftDTO = z.infer<typeof TicketReplyDraftSchema>

/**
 * Compact ticket summary returned by the reverse-reference lookup
 * (GET /tickets/linked/:moduleType/:moduleId) — enough to render a back-link
 * card on a linked record (e.g. a promoted task) with the ticket's current
 * stage, without exposing the full ticket payload.
 */
export interface LinkedTicketSummary {
  id: string
  ticketNumber: string
  subject: string
  stage: string
  stageTypeId: string | null
  /** Resolved stage display name (per-type, falling back to generic). */
  stageName: string | null
  stageColor: string | null
  priority: string
  isPrivate: boolean
  typeDef: { name: string; icon: string | null; color: string | null } | null
}

export interface TicketFilters {
  page?: number
  limit?: number
  /** Include lightweight polymorphic references in list rows. */
  includeReferences?: boolean
  /** Exclude resolved and closed tickets from list results. */
  hideClosed?: boolean
  /**
   * With hideClosed, still include tickets resolved/closed on or after this
   * ISO timestamp (e.g. "closed today" rows in My Work).
   */
  closedAfter?: string
  search?: string
  /** ScQL query string (Jira-style) — ANDed with the other filters */
  query?: string
  stage?: string
  stageTypeId?: string
  typeDefId?: string
  priority?: string
  source?: string
  assigneeIds?: string[]
  followerIds?: string[]
  requesterEmail?: string
  requesterEntityId?: string
  /**
   * Limit to the viewer's own tickets: 'submitted' = created/requested by
   * them, 'involved' = submitted plus tickets they follow (collaborator).
   */
  mine?: 'submitted' | 'involved'
  /**
   * Limit to tickets the viewer hasn't read (same definition as the sidebar
   * badge and per-row indicator: relevant + open + not opened since the last
   * activity). Viewer-relative, so it requires an authenticated user.
   */
  unread?: boolean
  referenceType?: string
  referenceId?: string
  dueDateFrom?: string
  dueDateTo?: string
  /**
   * Snoozed-ticket handling. "Snoozed" means snoozedUntil is in the future;
   * a past snoozedUntil is awake. Defaults server-side to HIDDEN when the
   * hideClosed default view applies (main agent lists), INCLUDE otherwise
   * (deep links, recycle bin, reporting).
   */
  snoozed?: 'HIDDEN' | 'ONLY' | 'INCLUDE'
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}
